(() => {
  if (window.ThemeCartSchedule) return;

  class ThemeCartSchedule {
    constructor(host) {
      this.host = host;
      this.form = host.querySelector('[data-cart-checkout-form]');
      this.dateInput = host.querySelector('[data-schedule-date]');
      this.timeFields = host.querySelectorAll('[data-schedule-time-field]');
      this.timeSelects = host.querySelectorAll('[data-schedule-time]');
      this.errorEl = host.querySelector('[data-schedule-error]');
      this.methodInputs = host.querySelectorAll('[data-schedule-method]');
      this.checkoutButton = this.form?.querySelector('[name="checkout"], [data-schedule-checkout]');

      if (!this.form || !this.dateInput) return;

      this.applyDateBounds();
      this.syncMethodUi();
      this.updateCheckoutState();
      this.bindEvents();
    }

    bindEvents() {
      this.methodInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.syncMethodUi({ resetTime: true });
          this.clearError();
          this.updateCheckoutState();
          this.saveAttributes();
        });
      });

      this.dateInput.addEventListener('change', () => {
        this.clearError();
        this.updateCheckoutState();
        this.saveAttributes();
      });

      this.dateInput.addEventListener('input', () => {
        this.clearError();
        this.updateCheckoutState();
      });

      this.timeSelects.forEach((select) => {
        select.addEventListener('change', () => {
          this.clearError();
          this.updateCheckoutState();
          this.saveAttributes();
        });
      });

      this.form.addEventListener('submit', (event) => {
        if (!this.validate()) {
          event.preventDefault();
          this.updateCheckoutState();
        }
      });
    }

    parseIsoDate(value) {
      if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
      const parts = value.split('-').map(Number);
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }

    formatIsoDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    addDays(date, days) {
      const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      next.setDate(next.getDate() + days);
      return next;
    }

    applyDateBounds() {
      const today = this.parseIsoDate(this.host.dataset.shopToday) || new Date();
      const campaignStart = this.parseIsoDate(this.host.dataset.campaignStart);
      const campaignEnd = this.parseIsoDate(this.host.dataset.campaignEnd);
      const leadCampaign = parseInt(this.host.dataset.leadDaysCampaign || '7', 10);
      const leadBefore = parseInt(this.host.dataset.leadDaysBefore || '0', 10);

      let leadDays = leadBefore;
      if (campaignStart && today >= campaignStart) {
        leadDays = leadCampaign;
      }

      let minDate = this.addDays(today, leadDays);

      if (campaignStart && campaignStart > minDate) {
        minDate = campaignStart;
      }

      this.dateInput.min = this.formatIsoDate(minDate);

      if (campaignEnd) {
        this.dateInput.max = this.formatIsoDate(campaignEnd);
      } else {
        this.dateInput.removeAttribute('max');
      }

      if (this.dateInput.value) {
        const selected = this.parseIsoDate(this.dateInput.value);
        if (selected && selected < minDate) this.dateInput.value = '';
        if (selected && campaignEnd && selected > campaignEnd) this.dateInput.value = '';
      }
    }

    getSelectedMethod() {
      const checked = this.host.querySelector('[data-schedule-method]:checked');
      return checked ? checked.value : 'Delivery';
    }

    getActiveTimeSelect() {
      const method = this.getSelectedMethod();
      return this.host.querySelector(`[data-schedule-time-for="${method}"]`);
    }

    syncMethodUi({ resetTime = false } = {}) {
      const method = this.getSelectedMethod();

      this.timeFields.forEach((field) => {
        const fieldMethod = field.dataset.scheduleTimeMethod;
        const isActive = fieldMethod === method;
        field.hidden = !isActive;

        const select = field.querySelector('[data-schedule-time]');
        if (!select) return;

        if (isActive) {
          select.setAttribute('name', 'attributes[Preferred time]');
          select.required = true;
          if (resetTime) select.value = '';
        } else {
          select.removeAttribute('name');
          select.required = false;
          if (resetTime) select.value = '';
        }
      });
    }

    isComplete() {
      const timeSelect = this.getActiveTimeSelect();
      const dateValue = this.dateInput?.value || '';
      const timeValue = timeSelect?.value || '';
      const min = this.dateInput?.min || '';
      const max = this.dateInput?.max || '';

      if (!dateValue) return false;
      if (min && dateValue < min) return false;
      if (max && dateValue > max) return false;
      if (!timeValue) return false;
      return true;
    }

    updateCheckoutState() {
      if (!this.checkoutButton) return;

      const complete = this.isComplete();
      this.checkoutButton.disabled = !complete;
      this.checkoutButton.setAttribute('aria-disabled', complete ? 'false' : 'true');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    }

    validate() {
      const messages = window.cartScheduleMessages || {};
      const timeSelect = this.getActiveTimeSelect();
      const dateValue = this.dateInput?.value || '';
      const timeValue = timeSelect?.value || '';
      const min = this.dateInput?.min || '';
      const max = this.dateInput?.max || '';

      if (!dateValue) {
        this.showError(messages.dateRequired || 'Please select a preferred date.');
        this.dateInput?.focus();
        return false;
      }

      if (min && dateValue < min) {
        this.showError(messages.dateTooSoon || 'Please select a later date.');
        this.dateInput?.focus();
        return false;
      }

      if (max && dateValue > max) {
        this.showError(messages.dateTooLate || 'Please select an earlier date.');
        this.dateInput?.focus();
        return false;
      }

      if (!timeValue) {
        this.showError(messages.timeRequired || 'Please select a preferred time.');
        timeSelect?.focus();
        return false;
      }

      this.clearError();
      return true;
    }

    async saveAttributes() {
      const method = this.getSelectedMethod();
      const timeSelect = this.getActiveTimeSelect();
      const attributes = {
        'Fulfillment method': method,
        'Preferred date': this.dateInput?.value || '',
        'Preferred time': timeSelect?.value || '',
      };

      try {
        await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes }),
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  window.ThemeCartSchedule = ThemeCartSchedule;
})();
